import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useLiveSocket } from "@/lib/useLiveSocket";
import { StatusBadge } from "@/components/StatusBadge";
import { Link } from "react-router-dom";
import { Play, Pause, Stop, Eye } from "@phosphor-icons/react";
import { estimateCampaignEta } from "@/lib/campaignEta";
import { useCampaignElapsed, formatElapsed } from "@/lib/campaignTimer";

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(0);

  const { connected } = useLiveSocket((msg) => {
    if (msg.type === "dashboard") {
      setData(msg.data);
      setLastUpdate(Date.now());
    }
  });

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const r = await api.get("/dashboard");
        if (mounted) { setData(r.data); setLastUpdate(Date.now()); }
      } catch (_e) { /* ignore */ }
    };
    load();
    // Polling fallback ONLY when WS isn't connected
    const iv = setInterval(() => { if (!connected) load(); }, 4000);
    return () => { mounted = false; clearInterval(iv); };
  }, [connected]);

  const secondsAgo = lastUpdate ? Math.floor((Date.now() - lastUpdate) / 1000) : null;

  const control = async (id, action) => {
    await api.post(`/campaigns/${id}/${action}`);
  };

  return (
    <div className="p-8">
      <div className="flex items-baseline justify-between mb-8">
        <div>
          <div className="micro mb-1">Overview</div>
          <h1 className="font-heading text-4xl font-bold tracking-tighter">Command Dashboard</h1>
        </div>
        <div className="font-mono text-[11px] text-[#71717A]" data-testid="dashboard-live-indicator">
          [ {connected ? <span className="text-[#E1FF00]">live · ws</span> : <span className="text-[#FF9500]">polling</span>} · refreshed {secondsAgo ?? "—"}s ago ]
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 border border-[#2A2A2A] bg-[#0A0A0A]">
        <Stat label="Mailboxes" v={data?.total_senders} sub={`${data?.active_senders ?? 0} active`} />
        <Stat label="Google" v={data?.google_senders} />
        <Stat label="Microsoft" v={data?.microsoft_senders} />
        <Stat label="Recipients" v={data?.total_recipients} />
        <Stat label="Valid proxy" v={data?.valid_proxy} tone="success" />
        <Stat label="Failed proxy" v={data?.failed_proxy} tone="error" />
        <Stat label="Missing proxy" v={data?.missing_proxy} tone="warning" />
        <Stat label="Queue size" v={data?.queue_size} />
        <Stat label="Active campaigns" v={data?.active_campaigns} tone="brand" />
        <Stat label="Paused" v={data?.paused_campaigns} tone="warning" />
        <Stat label="Active workers" v={data?.active_workers} />
        <Stat label="Processed today" v={data?.processed_today} />
        <Stat label="Accepted today" v={data?.accepted_today} tone="success" />
        <Stat label="Failed today" v={data?.failed_today} tone="error" />
        <Stat label="Proxy fails today" v={data?.proxy_failed_today} tone="error" />
        <Stat label="Live workers" v={data?.active_workers} tone="brand" />
      </div>

      <div className="mt-10">
        <div className="flex items-baseline justify-between mb-4">
          <div>
            <div className="micro mb-1">Active</div>
            <h2 className="font-heading text-2xl font-semibold tracking-tight">Running campaigns</h2>
          </div>
        </div>

        <div className="border border-[#2A2A2A] bg-[#0A0A0A] overflow-x-auto">
          <table className="dtable w-full" data-testid="dashboard-campaigns-table">
            <thead>
              <tr>
                <th>Campaign</th>
                <th>Status</th>
                <th className="num">Recipients</th>
                <th className="num" style={{ minWidth: 180 }}>Progress</th>
                <th className="num">Accepted</th>
                <th className="num">Failed</th>
                <th className="num">Proxy fail</th>
                <th className="num">Batch</th>
                <th>Workers</th>
                <th>Elapsed</th>
                <th>ETA</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {(data?.campaigns || []).length === 0 && (
                <tr><td colSpan={12} className="text-center text-[#71717A] py-8 font-mono">[ no active campaigns ]</td></tr>
              )}
              {(data?.campaigns || []).map((c) => (
                <CampaignRow key={c.id} c={c} onControl={control} />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function CampaignRow({ c, onControl }) {
  const eta = estimateCampaignEta(c);
  const pct = eta.percent ?? 0;
  const { seconds } = useCampaignElapsed(c);
  return (
    <tr>
      <td className="font-medium">
        <Link to={`/campaigns/${c.id}`} className="hover:text-[#E1FF00]" data-testid={`campaign-link-${c.id}`}>
          {c.name}
        </Link>
      </td>
      <td><StatusBadge status={c.status} /></td>
      <td className="num data-num" data-testid={`recipients-${c.id}`}>{c.total ?? 0}</td>
      <td className="num" data-testid={`progress-${c.id}`}>
        <div className="flex items-center gap-2 justify-end mb-1">
          <span className="data-num text-[11px] text-[#71717A]">{c.total_processed}/{c.total ?? 0}</span>
          <span className="data-num text-[11px] font-bold text-[#E1FF00]">{pct}%</span>
        </div>
        <div className="h-1.5 w-full bg-[#141414] overflow-hidden">
          <div className="h-full bg-[#E1FF00] transition-all duration-300" style={{ width: `${pct}%` }} />
        </div>
      </td>
      <td className="num data-num text-[#00FF66]">{c.total_accepted}</td>
      <td className="num data-num text-[#FF3B30]">{c.total_failed}</td>
      <td className="num data-num text-[#FF9500]">{c.total_proxy_failed}</td>
      <td className="num data-num">{c.current_batch_number || 1}</td>
      <td className="data-num">{c.active_workers || 0}</td>
      <td className="data-num font-mono text-[11px]" data-testid={`elapsed-${c.id}`}>
        <span className={c.status === "completed" ? "text-[#00FF66]" : c.status === "stopped" ? "text-[#A1A1AA]" : "text-[#E1FF00]"}>
          {formatElapsed(seconds)}
        </span>
      </td>
      <td className="data-num" data-testid={`eta-${c.id}`}>
        <span className={eta.method === "measured" ? "text-[#00E5FF]" : "text-[#A1A1AA]"}>
          {eta.human}
        </span>
        <span className="text-[10px] text-[#71717A] block leading-none">
          {eta.method === "measured" ? "live" : "est"}
        </span>
      </td>
      <td>
        <div className="flex gap-1">
          {c.status === "running" && (
            <IconBtn testid={`pause-${c.id}`} onClick={() => onControl(c.id, "pause")}><Pause size={12} weight="fill" /></IconBtn>
          )}
          {c.status === "paused" && (
            <IconBtn testid={`resume-${c.id}`} onClick={() => onControl(c.id, "resume")}><Play size={12} weight="fill" /></IconBtn>
          )}
          <IconBtn testid={`stop-${c.id}`} onClick={() => onControl(c.id, "stop")}><Stop size={12} weight="fill" /></IconBtn>
          <Link to={`/campaigns/${c.id}`}>
            <IconBtn testid={`view-${c.id}`}><Eye size={12} weight="bold" /></IconBtn>
          </Link>
        </div>
      </td>
    </tr>
  );
}

function Stat({ label, v, sub, tone }) {
  const c = tone === "success" ? "text-[#00FF66]" :
            tone === "error" ? "text-[#FF3B30]" :
            tone === "warning" ? "text-[#FF9500]" :
            tone === "brand" ? "text-[#E1FF00]" : "text-white";
  return (
    <div className="border-r border-b border-[#2A2A2A] last:border-r-0 p-4 min-h-[92px]">
      <div className="micro mb-2">{label}</div>
      <div className={`data-num text-2xl font-bold ${c}`}>{v ?? "—"}</div>
      {sub && <div className="micro mt-1 text-[#71717A]">{sub}</div>}
    </div>
  );
}

function IconBtn({ children, onClick, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className="w-7 h-7 flex items-center justify-center border border-[#2A2A2A] hover:border-[#E1FF00] hover:text-[#E1FF00] text-[#A1A1AA] transition-colors"
    >
      {children}
    </button>
  );
}
